import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useDispatch} from 'react-redux';

import {isNil, isEmpty, head, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteServiceOrderIssueNoteMutation,
  usePatchServiceOrderIssueNotePartsReadyMutation,
  usePostPrintoutOnDemandMutation,
} from '@dms/api/warehouse';
import {metadaWorkshopServiceOrderApi} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  downloadFile,
  handleApiError,
  logError,
  printPDF,
  useFileNotification,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

interface ServiceCaseOrderIssuesDataGridProps {
  maximization?: EagGridMaximization;
  orderId: string;
}

const PREDEFINED_COLUMNS = ['partsReady'] as const;
export function ServiceCaseOrderIssuesDataGrid(props: ServiceCaseOrderIssuesDataGridProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The hook drops the per-file `onError` on timeout, so a stuck printout needs its own handler
  const {registerFile} = useFileNotification({onTimeout: () => showNotification.error()});

  const [patchServiceOrderIssueNotePartsReady] = usePatchServiceOrderIssueNotePartsReadyMutation();
  const [deleteServiceOrderIssueNote] = useDeleteServiceOrderIssueNoteMutation();
  const [postPrintoutOnDemand] = usePostPrintoutOnDemandMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {serviceOrderId: props.orderId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const selectedRowId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(selectedRowId)) {
      return logError('Selected row id is undefined');
    }

    // Returned so the grid locks the action until the request settles; the file wait that
    // follows is not covered
    return match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(
          composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
            params: {
              id: selectedRowId,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('ready', 'remove-ready', async () => {
        const arePartsReady = getColumnData<boolean>('partsReady')?.value;

        if (isNil(arePartsReady)) {
          return logError('Parts ready is not defined');
        }

        await patchServiceOrderIssueNotePartsReady({
          serviceOrderIssueNoteId: selectedRowId,
          body: {partsReady: actionKey === 'ready'},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('entity.warehouse.actions.deleteEntity'),
          onConfirm: () =>
            deleteServiceOrderIssueNote({serviceOrderIssueNoteId: selectedRowId})
              .unwrap()
              .then(() =>
                dispatch(
                  metadaWorkshopServiceOrderApi.util.invalidateTags([
                    {type: 'partMovementsCount', id: props.orderId},
                  ])
                )
              )
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .with('print', 'download', (key) =>
        postPrintoutOnDemand({
          body: {
            docOriginId: selectedRowId,
            originEntityPrintoutType: 'prntServiceOrderIssueNote',
          },
        })
          .unwrap()
          .then((data) => {
            const fileId = data?.printout?.fileId;
            const fileUrl = data?.printout?.pdfUrl;

            // Both consumers call `new URL(fileUrl)`, which throws synchronously on an empty string
            if (isNil(fileId) || isNil(fileUrl) || isEmpty(fileId) || isEmpty(fileUrl)) {
              return showNotification.error();
            }

            registerFile(fileId, {
              onSuccess: () =>
                key === 'print'
                  ? printPDF(fileUrl)
                  : downloadFile(fileUrl, {fileType: 'attachment'}),
              onError: () => showNotification.error(),
            });
          })
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      maximization={props.maximization}
      autoHeight
      gridCode="service-case-order-issues"
      data-testid="serviceOrderIssues"
      queryModifier={queryModifier}
      actionCallback={actionCallback}
      predefinedColumns={PREDEFINED_COLUMNS}
    />
  );
}
